import { TranslocoPipe } from '@jsverse/transloco';
import { Component } from '@angular/core';
import { RevealDirective } from '../../../shared/animations/reveal.directive';
import { MatchdayCardComponent } from '../../trainings/components/matchday-card.component';
import { CommunityTrainingTimetableComponent } from '../../trainings/components/community-training-timetable.component';
import { DEMO_TRAINING, DEMO_TRAININGS } from '../domain/landing-demo-data';

/**
 * The second landing pillar: community & training management. Mirrors landing-feature-rows' layout
 * (section-heading + alternating feature-row grid). Training previews use the same feature-local
 * presentation components as the signed-in app, fed only staged demo data (no facade or repository).
 */
@Component({
  selector: 'app-landing-club-showcase',
  standalone: true,
  imports: [TranslocoPipe, RevealDirective, MatchdayCardComponent, CommunityTrainingTimetableComponent],
  template: `
    <section class="club-showcase" aria-labelledby="club-showcase-title">
      <div class="section-heading" appReveal>
        <span>{{ 'landing.clubTrainingManagement' | transloco }}</span>
        <h2 id="club-showcase-title">{{ 'landing.runYourClubsTrainingsNotSpreadsheets' | transloco }}</h2>
      </div>

      <article class="feature-row feature-row--flipped" appReveal [appRevealIndex]="1">
        <div class="feature-copy">
          <span>{{ 'landing.matchdayRsvp' | transloco }}</span>
          <h3>{{ 'landing.oneTapAndEveryoneKnowsWhosComing' | transloco }}</h3>
          <p>{{ 'landing.postASessionCollectOneTapRsvpsAndLet' | transloco }}</p>
          <strong>{{ 'landing.copy8Going2OnTheWaitlist' | transloco }}</strong>
        </div>
        <div class="feature-visual" aria-hidden="true">
          <app-matchday-card
            class="training-preview"
            [training]="demoTraining"
            [titleOverride]="'landing.clubTraining' | transloco"
            viewer="preview"
            density="standard"
            [interactive]="false"
          />
        </div>
      </article>

      <article class="feature-row" appReveal [appRevealIndex]="2">
        <div class="feature-copy">
          <span>{{ 'landing.liveRollCall' | transloco }}</span>
          <h3>{{ 'landing.checkPlayersInGetBalancedGamesInstantly' | transloco }}</h3>
          <p>{{ 'landing.checkInPlayersAndGuestsThenBuildFairCourts' | transloco }}</p>
          <strong>{{ 'landing.checkedIn123CourtsGenerated' | transloco }}</strong>
        </div>
        <div class="feature-visual" aria-hidden="true">
          <div class="mock mock-rollcall">
            <p class="mock-eyebrow">{{ 'landing.checkedIn12' | transloco }}</p>
            <ul class="mock-list">
              <li><span class="tick"></span>{{ 'landing.jonasP' | transloco }}</li>
              <li><span class="tick"></span>{{ 'landing.miraK' | transloco }}</li>
              <li><span class="tick"></span>{{ 'landing.leonS' | transloco }} <em>{{ 'landing.guest' | transloco }}</em></li>
              <li><span class="tick"></span>{{ 'landing.ruthA' | transloco }}</li>
            </ul>
            <div class="mock-courts">
              <span class="court">{{ 'landing.court1' | transloco }}</span>
              <span class="court">{{ 'landing.court2' | transloco }}</span>
              <span class="court">{{ 'landing.court3' | transloco }}</span>
            </div>
            <span class="btn btn-primary mock-build">{{ 'landing.buildTheGames' | transloco }}</span>
          </div>
        </div>
      </article>

      <article class="feature-row feature-row--flipped" appReveal [appRevealIndex]="3">
        <div class="feature-copy">
          <span>{{ 'landing.communitySchedule' | transloco }}</span>
          <h3>{{ 'landing.setTheSeasonOnceStayOnTheMap' | transloco }}</h3>
          <p>{{ 'landing.scheduleRecurringSessionsAndHelpNearbyPlayersFindYour' | transloco }}</p>
          <strong>{{ 'landing.weeklySeriesCommunityMap' | transloco }}</strong>
        </div>
        <div class="feature-visual" aria-hidden="true">
          <app-community-training-timetable
            class="schedule-preview"
            [trainings]="demoTrainings"
            [interactive]="false"
          />
        </div>
      </article>
    </section>
  `,
  styles: [`
    .club-showcase {
      display: grid;
      gap: clamp(2rem, 5vw, 3.5rem);
      margin-inline: auto;
      width: min(100%, 74rem);
    }

    .section-heading {
      display: grid;
      gap: var(--space-2);
      max-width: 44rem;
    }

    .section-heading span,
    .feature-copy span {
      color: var(--color-primary);
      font-size: var(--font-size-label);
      font-weight: 900;
      text-transform: uppercase;
    }

    h2, h3, p { margin: 0; }

    h2 {
      font-size: clamp(1.75rem, 4vw, 3.2rem);
      letter-spacing: 0;
      line-height: 1.05;
    }

    .feature-row {
      align-items: center;
      background: var(--color-surface);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      display: grid;
      gap: var(--space-6);
      grid-template-columns: minmax(16rem, 0.75fr) minmax(0, 1fr);
      padding: clamp(1rem, 3vw, 2rem);
    }

    .feature-row--flipped .feature-copy { order: 2; }
    .feature-row--flipped .feature-visual { order: 1; }

    .feature-copy { display: grid; gap: var(--space-3); }

    h3 {
      font-size: clamp(1.5rem, 3vw, 2.3rem);
      letter-spacing: 0;
      line-height: 1.08;
    }

    .feature-copy p { color: var(--color-text-secondary); line-height: 1.7; }
    .feature-copy strong { font-size: 1.05rem; font-weight: 900; }

    .feature-visual {
      background: var(--color-surface-high);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-card);
      display: grid;
      min-height: 18rem;
      min-width: 0;
      padding: clamp(1rem, 3vw, 1.75rem);
      place-items: center;
    }

    /* Shared mock chrome */
    .mock {
      background: var(--color-surface);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-card);
      display: grid;
      gap: var(--space-4);
      padding: var(--space-5);
      width: min(100%, 22rem);
    }

    .mock-eyebrow {
      color: var(--color-text-secondary);
      font-size: var(--font-size-label);
      font-weight: 900;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .btn {
      align-items: center;
      border-radius: var(--radius-md);
      display: inline-flex;
      font-size: var(--font-size-caption);
      font-weight: 900;
      justify-content: center;
      min-height: 2.4rem;
      padding-inline: var(--space-4);
    }

    .btn-primary { background: var(--color-action, var(--color-primary)); color: var(--color-on-action, #fff); }
    /* Roll-call mock */
    .mock-list { display: grid; gap: var(--space-2); list-style: none; margin: 0; padding: 0; }
    .mock-list li {
      align-items: center;
      color: var(--color-text-primary);
      display: flex;
      font-weight: 800;
      gap: var(--space-3);
    }
    .mock-list em { color: var(--color-text-secondary); font-style: normal; font-weight: 700; opacity: 0.8; }
    .tick {
      background: var(--color-success);
      border-radius: 50%;
      flex-shrink: 0;
      height: 1.1rem;
      position: relative;
      width: 1.1rem;
    }
    .tick::after {
      border: solid #fff;
      border-width: 0 0.14rem 0.14rem 0;
      content: '';
      height: 0.5rem;
      left: 0.38rem;
      position: absolute;
      top: 0.16rem;
      transform: rotate(45deg);
      width: 0.28rem;
    }
    .mock-courts { display: flex; flex-wrap: wrap; gap: var(--space-2); }
    .court {
      background: var(--color-surface-high);
      border: 1px dashed var(--color-border-subtle);
      border-radius: var(--radius-md);
      font-size: var(--font-size-caption);
      font-weight: 900;
      padding: 0.35rem 0.7rem;
    }
    .mock-build { width: 100%; }

    .training-preview, .schedule-preview { display: block; min-width: 0; width: 100%; }

    @media (max-width: 900px) {
      .feature-row { grid-template-columns: 1fr; }
      .feature-row--flipped .feature-copy { order: 1; }
      .feature-row--flipped .feature-visual { order: 2; }
    }
  `],
})
export class LandingClubShowcaseComponent {
  readonly demoTraining = DEMO_TRAINING;
  readonly demoTrainings = DEMO_TRAININGS;
}
